import { countries } from "countries-list"

/**
 * ISO 4217 display metadata for currencies returned by Frankfurter / stored in `currency_exchange_rates`.
 * Flag uses regional indicators where applicable; name matches Frankfurter's English label.
 * Covers 165 codes (Frankfurter active list, including USD).
 */
export type CurrencyMeta = {
  flag: string
  name: string
}

export const CURRENCY_META: Record<string, CurrencyMeta> = {
  "AED": { flag: "🇦🇪", name: "United Arab Emirates Dirham" },
  "AFN": { flag: "🇦🇫", name: "Afghan Afghani" },
  "ALL": { flag: "🇦🇱", name: "Albanian Lek" },
  "AMD": { flag: "🇦🇲", name: "Armenian Dram" },
  "ANG": { flag: "🇨🇼", name: "Netherlands Antillean Gulden" },
  "AOA": { flag: "🇦🇴", name: "Angolan Kwanza" },
  "ARS": { flag: "🇦🇷", name: "Argentine Peso" },
  "AUD": { flag: "🇦🇺", name: "Australian Dollar" },
  "AWG": { flag: "🇦🇼", name: "Aruban Florin" },
  "AZN": { flag: "🇦🇿", name: "Azerbaijani Manat" },
  "BAM": { flag: "🇧🇦", name: "Bosnia and Herzegovina Convertible Mark" },
  "BBD": { flag: "🇧🇧", name: "Barbadian Dollar" },
  "BDT": { flag: "🇧🇩", name: "Bangladeshi Taka" },
  "BGN": { flag: "🇧🇬", name: "Bulgarian Lev" },
  "BHD": { flag: "🇧🇭", name: "Bahraini Dinar" },
  "BIF": { flag: "🇧🇮", name: "Burundian Franc" },
  "BMD": { flag: "🇧🇲", name: "Bermudian Dollar" },
  "BND": { flag: "🇧🇳", name: "Brunei Dollar" },
  "BOB": { flag: "🇧🇴", name: "Bolivian Boliviano" },
  "BRL": { flag: "🇧🇷", name: "Brazilian Real" },
  "BSD": { flag: "🇧🇸", name: "Bahamian Dollar" },
  "BTN": { flag: "🇧🇹", name: "Bhutanese Ngultrum" },
  "BWP": { flag: "🇧🇼", name: "Botswana Pula" },
  "BYN": { flag: "🇧🇾", name: "Belarusian Ruble" },
  "BZD": { flag: "🇧🇿", name: "Belize Dollar" },
  "CAD": { flag: "🇨🇦", name: "Canadian Dollar" },
  "CDF": { flag: "🇨🇩", name: "Congolese Franc" },
  "CHF": { flag: "🇨🇭", name: "Swiss Franc" },
  "CLP": { flag: "🇨🇱", name: "Chilean Peso" },
  "CNH": { flag: "🇨🇳", name: "Chinese Renminbi Yuan Offshore" },
  "CNY": { flag: "🇨🇳", name: "Chinese Renminbi Yuan" },
  "COP": { flag: "🇨🇴", name: "Colombian Peso" },
  "CRC": { flag: "🇨🇷", name: "Costa Rican Colón" },
  "CUP": { flag: "🇨🇺", name: "Cuban Peso" },
  "CVE": { flag: "🇨🇻", name: "Cape Verdean Escudo" },
  "CZK": { flag: "🇨🇿", name: "Czech Koruna" },
  "DJF": { flag: "🇩🇯", name: "Djiboutian Franc" },
  "DKK": { flag: "🇩🇰", name: "Danish Krone" },
  "DOP": { flag: "🇩🇴", name: "Dominican Peso" },
  "DZD": { flag: "🇩🇿", name: "Algerian Dinar" },
  "EGP": { flag: "🇪🇬", name: "Egyptian Pound" },
  "ERN": { flag: "🇪🇷", name: "Eritrean Nakfa" },
  "ETB": { flag: "🇪🇹", name: "Ethiopian Birr" },
  "EUR": { flag: "🇪🇺", name: "Euro" },
  "FJD": { flag: "🇫🇯", name: "Fijian Dollar" },
  "FKP": { flag: "🇫🇰", name: "Falkland Pound" },
  "GBP": { flag: "🇬🇧", name: "British Pound" },
  "GEL": { flag: "🇬🇪", name: "Georgian Lari" },
  "GGP": { flag: "🇬🇬", name: "Guernsey Pound" },
  "GHS": { flag: "🇬🇭", name: "Ghanaian Cedi" },
  "GIP": { flag: "🇬🇮", name: "Gibraltar Pound" },
  "GMD": { flag: "🇬🇲", name: "Gambian Dalasi" },
  "GNF": { flag: "🇬🇳", name: "Guinean Franc" },
  "GTQ": { flag: "🇬🇹", name: "Guatemalan Quetzal" },
  "GYD": { flag: "🇬🇾", name: "Guyanese Dollar" },
  "HKD": { flag: "🇭🇰", name: "Hong Kong Dollar" },
  "HNL": { flag: "🇭🇳", name: "Honduran Lempira" },
  "HTG": { flag: "🇭🇹", name: "Haitian Gourde" },
  "HUF": { flag: "🇭🇺", name: "Hungarian Forint" },
  "IDR": { flag: "🇮🇩", name: "Indonesian Rupiah" },
  "ILS": { flag: "🇮🇱", name: "Israeli New Shekel" },
  "IMP": { flag: "🇮🇲", name: "Isle of Man Pound" },
  "INR": { flag: "🇮🇳", name: "Indian Rupee" },
  "IQD": { flag: "🇮🇶", name: "Iraqi Dinar" },
  "IRR": { flag: "🇮🇷", name: "Iranian Rial" },
  "ISK": { flag: "🇮🇸", name: "Icelandic Króna" },
  "JEP": { flag: "🇯🇪", name: "Jersey Pound" },
  "JMD": { flag: "🇯🇲", name: "Jamaican Dollar" },
  "JOD": { flag: "🇯🇴", name: "Jordanian Dinar" },
  "JPY": { flag: "🇯🇵", name: "Japanese Yen" },
  "KES": { flag: "🇰🇪", name: "Kenyan Shilling" },
  "KGS": { flag: "🇰🇬", name: "Kyrgyzstani Som" },
  "KHR": { flag: "🇰🇭", name: "Cambodian Riel" },
  "KMF": { flag: "🇰🇲", name: "Comorian Franc" },
  "KRW": { flag: "🇰🇷", name: "South Korean Won" },
  "KWD": { flag: "🇰🇼", name: "Kuwaiti Dinar" },
  "KYD": { flag: "🇰🇾", name: "Cayman Islands Dollar" },
  "KZT": { flag: "🇰🇿", name: "Kazakhstani Tenge" },
  "LAK": { flag: "🇱🇦", name: "Lao Kip" },
  "LBP": { flag: "🇱🇧", name: "Lebanese Pound" },
  "LKR": { flag: "🇱🇰", name: "Sri Lankan Rupee" },
  "LRD": { flag: "🇱🇷", name: "Liberian Dollar" },
  "LSL": { flag: "🇱🇸", name: "Lesotho Loti" },
  "LYD": { flag: "🇱🇾", name: "Libyan Dinar" },
  "MAD": { flag: "🇲🇦", name: "Moroccan Dirham" },
  "MDL": { flag: "🇲🇩", name: "Moldovan Leu" },
  "MGA": { flag: "🇲🇬", name: "Malagasy Ariary" },
  "MKD": { flag: "🇲🇰", name: "Macedonian Denar" },
  "MMK": { flag: "🇲🇲", name: "Myanmar Kyat" },
  "MNT": { flag: "🇲🇳", name: "Mongolian Tögrög" },
  "MOP": { flag: "🇲🇴", name: "Macanese Pataca" },
  "MRO": { flag: "🇲🇷", name: "Mauritanian Ouguiya" },
  "MRU": { flag: "🇲🇷", name: "Mauritanian Ouguiya" },
  "MUR": { flag: "🇲🇺", name: "Mauritian Rupee" },
  "MVR": { flag: "🇲🇻", name: "Maldivian Rufiyaa" },
  "MWK": { flag: "🇲🇼", name: "Malawian Kwacha" },
  "MXN": { flag: "🇲🇽", name: "Mexican Peso" },
  "MYR": { flag: "🇲🇾", name: "Malaysian Ringgit" },
  "MZN": { flag: "🇲🇿", name: "Mozambican Metical" },
  "NAD": { flag: "🇳🇦", name: "Namibian Dollar" },
  "NGN": { flag: "🇳🇬", name: "Nigerian Naira" },
  "NIO": { flag: "🇳🇮", name: "Nicaraguan Córdoba" },
  "NOK": { flag: "🇳🇴", name: "Norwegian Krone" },
  "NPR": { flag: "🇳🇵", name: "Nepalese Rupee" },
  "NZD": { flag: "🇳🇿", name: "New Zealand Dollar" },
  "OMR": { flag: "🇴🇲", name: "Omani Rial" },
  "PAB": { flag: "🇵🇦", name: "Panamanian Balboa" },
  "PEN": { flag: "🇵🇪", name: "Peruvian Sol" },
  "PGK": { flag: "🇵🇬", name: "Papua New Guinean Kina" },
  "PHP": { flag: "🇵🇭", name: "Philippine Peso" },
  "PKR": { flag: "🇵🇰", name: "Pakistani Rupee" },
  "PLN": { flag: "🇵🇱", name: "Polish Złoty" },
  "PYG": { flag: "🇵🇾", name: "Paraguayan Guaraní" },
  "QAR": { flag: "🇶🇦", name: "Qatari Riyal" },
  "RON": { flag: "🇷🇴", name: "Romanian Leu" },
  "RSD": { flag: "🇷🇸", name: "Serbian Dinar" },
  "RUB": { flag: "🇷🇺", name: "Russian Ruble" },
  "RWF": { flag: "🇷🇼", name: "Rwandan Franc" },
  "SAR": { flag: "🇸🇦", name: "Saudi Riyal" },
  "SBD": { flag: "🇸🇧", name: "Solomon Islands Dollar" },
  "SCR": { flag: "🇸🇨", name: "Seychellois Rupee" },
  "SDG": { flag: "🇸🇩", name: "Sudanese Pound" },
  "SEK": { flag: "🇸🇪", name: "Swedish Krona" },
  "SGD": { flag: "🇸🇬", name: "Singapore Dollar" },
  "SHP": { flag: "🇸🇭", name: "Saint Helenian Pound" },
  "SLE": { flag: "🇸🇱", name: "New Leone" },
  "SOS": { flag: "🇸🇴", name: "Somali Shilling" },
  "SRD": { flag: "🇸🇷", name: "Surinamese Dollar" },
  "SSP": { flag: "🇸🇸", name: "South Sudanese Pound" },
  "STN": { flag: "🇸🇹", name: "São Tomé and Príncipe Second Dobra" },
  "SVC": { flag: "🇸🇻", name: "Salvadoran Colón" },
  "SYP": { flag: "🇸🇾", name: "Syrian Pound" },
  "SZL": { flag: "🇸🇿", name: "Swazi Lilangeni" },
  "THB": { flag: "🇹🇭", name: "Thai Baht" },
  "TJS": { flag: "🇹🇯", name: "Tajikistani Somoni" },
  "TMT": { flag: "🇹🇲", name: "Turkmenistani Manat" },
  "TND": { flag: "🇹🇳", name: "Tunisian Dinar" },
  "TOP": { flag: "🇹🇴", name: "Tongan Paʻanga" },
  "TRY": { flag: "🇹🇷", name: "Turkish Lira" },
  "TTD": { flag: "🇹🇹", name: "Trinidad and Tobago Dollar" },
  "TWD": { flag: "🇹🇼", name: "New Taiwan Dollar" },
  "TZS": { flag: "🇹🇿", name: "Tanzanian Shilling" },
  "UAH": { flag: "🇺🇦", name: "Ukrainian Hryvnia" },
  "UGX": { flag: "🇺🇬", name: "Ugandan Shilling" },
  "USD": { flag: "🇺🇸", name: "US Dollar" },
  "UYU": { flag: "🇺🇾", name: "Uruguayan Peso" },
  "UZS": { flag: "🇺🇿", name: "Uzbekistan Som" },
  "VES": { flag: "🇻🇪", name: "Venezuelan Bolívar Soberano" },
  "VND": { flag: "🇻🇳", name: "Vietnamese Đồng" },
  "VUV": { flag: "🇻🇺", name: "Vanuatu Vatu" },
  "WST": { flag: "🇼🇸", name: "Samoan Tala" },
  "XAF": { flag: "🇨🇲", name: "Central African Cfa Franc" },
  "XAG": { flag: "🇺🇸", name: "Silver (Troy Ounce)" },
  "XAU": { flag: "🇺🇸", name: "Gold (Troy Ounce)" },
  "XCD": { flag: "🇦🇬", name: "East Caribbean Dollar" },
  "XCG": { flag: "🇦🇬", name: "Caribbean Guilder" },
  "XDR": { flag: "🇺🇸", name: "Special Drawing Rights" },
  "XOF": { flag: "🇸🇳", name: "West African Cfa Franc" },
  "XPD": { flag: "🇺🇸", name: "Palladium" },
  "XPF": { flag: "🇵🇫", name: "Cfp Franc" },
  "XPT": { flag: "🇺🇸", name: "Platinum" },
  "YER": { flag: "🇾🇪", name: "Yemeni Rial" },
  "ZAR": { flag: "🇿🇦", name: "South African Rand" },
  "ZMW": { flag: "🇿🇲", name: "Zambian Kwacha" },
  "ZWG": { flag: "🇿🇼", name: "Zimbabwe Gold" },
}

export function getCurrencyMeta(code: string): CurrencyMeta | undefined {
  const c = code.trim().toUpperCase()
  if (c.length !== 3) return undefined
  return CURRENCY_META[c]
}

/** English country names that list this ISO 4217 code in `countries-list` (for combobox search). */
export function countryNamesForCurrency(iso4217: string): string[] {
  const target = iso4217.trim().toUpperCase()
  if (target.length !== 3) return []
  const names = new Set<string>()
  for (const row of Object.values(countries)) {
    const curs = row.currency
    if (!Array.isArray(curs)) continue
    if (curs.some((x) => String(x).toUpperCase() === target)) {
      names.add(row.name)
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
}
